import {
  useCurrentRefinements,
  Stats,
  useClearRefinements,
} from 'react-instantsearch';
import {
  getRefinements,
  getRefinementsOptions,
  getRefinementsScoresOptions,
} from '@monofront/business/utils';
import { useTranslations } from 'next-intl';
import Close from '@monofront/design-system/visuals/Icons/Close';
import { ALGOLIA_FACET_VALUES } from '@monofront/business/constants/algolia';

type CurrentRefinementsProps = {
  withTotalHits?: boolean;
};

const CurrentRefinements: React.FC<CurrentRefinementsProps> = ({
  withTotalHits,
}) => {
  const { items, refine } = useCurrentRefinements();
  const { refine: clearRefinements } = useClearRefinements({
    includedAttributes: [
      'categories',
      'shelfs',
      'values',
      'vendor',
      'meta.marketing.nutriscore_grade',
      'meta.marketing.environmentalscore_grade',
    ],
  });

  const {
    refinementsVendor,
    refinementsValues,
    refinementsCategories,
    refinementsShelfs,
    refinementsNutriscore,
    refinementsEnvironmentalscore,
    refinementsDiets,
  } = getRefinements(items);

  const refinements = getRefinementsOptions([
    refinementsVendor,
    refinementsCategories,
    refinementsShelfs,
    refinementsValues,
    refinementsDiets,
  ]);

  const refinementsScores = getRefinementsScoresOptions([
    refinementsNutriscore,
    refinementsEnvironmentalscore,
  ]);

  const tCatalog = useTranslations('Catalog');
  const tGlobal = useTranslations('Global');

  return (
    <div className="tw--m-1 desktop:tw--m-2">
      {withTotalHits && (
        <div
          className="tw-lf-body-regular-sm tw-hidden tw-p-1 desktop:tw-inline desktop:tw-p-2 desktop:[&>*]:tw-inline"
          aria-live="polite"
        >
          <Stats
            translations={{
              rootElementText({ nbHits }) {
                return `${nbHits} ${tCatalog('results')}`;
              },
            }}
          />
        </div>
      )}

      <section className="tw-inline" aria-label={tCatalog('activeFilters')}>
        <ul className={`tw-inline`}>
          {refinements.length > 0 &&
            refinements.map(
              (item) =>
                item?.refinements &&
                item.refinements.map((nestedItem) => (
                  <li
                    key={nestedItem.label}
                    className={`tw-inline-block tw-p-1 desktop:tw-p-2`}
                  >
                    <div className="tw-lf-chip tw-lf-chip-primary tw-inline-flex">
                      <span className="tw-lf-body-regular-sm">
                        {nestedItem.label}
                      </span>
                      <button
                        className="tw-lf-button-clear tw-ml-2 tw-mr-0.5"
                        aria-label={tGlobal('delete')}
                        onClick={(event) => {
                          event.preventDefault();
                          refine(nestedItem);
                        }}
                      >
                        <Close width={8} height={8} aria-hidden />
                      </button>
                    </div>
                  </li>
                )),
            )}
          {refinementsScores.length > 0 &&
            refinementsScores.map(
              (item) =>
                item?.refinements &&
                item.refinements.map((nestedItem) => (
                  <li
                    key={nestedItem.label}
                    className={`tw-inline-block tw-p-1 desktop:tw-p-2`}
                  >
                    <div className="tw-lf-chip tw-lf-chip-primary tw-inline-flex">
                      <span className="tw-lf-body-regular-sm">
                        {item.attribute === 'meta.marketing.environmentalscore_grade'
                          ? tCatalog('greenscore')
                          : tCatalog('nutriscore')}
                        : {ALGOLIA_FACET_VALUES[nestedItem.label] ? ALGOLIA_FACET_VALUES[nestedItem.label] : nestedItem.label}
                      </span>
                      <button
                        className="tw-lf-button-clear tw-ml-2 tw-mr-0.5"
                        aria-label={tGlobal('delete')}
                        onClick={(event) => {
                          event.preventDefault();
                          refine(nestedItem);
                        }}
                      >
                        <Close width={8} height={8} aria-hidden />
                      </button>
                    </div>
                  </li>
                )),
            )}
        </ul>
        {refinementsScores.length > 0 || refinements.length > 0 ? (
          <button
            type="button"
            className="tw-lf-link-sm tw-p-2"
            onClick={() => (items.length ? clearRefinements() : {})}
            data-testid="ClearRefinements-go-to-clear-filters"
          >
            {tCatalog('resetFilters')}
          </button>
        ) : null}
      </section>
    </div>
  );
};

export default CurrentRefinements;
